import { useState, type ReactNode } from 'react';
import { ApiError } from './api.js';

/** Bloco de formulário em duas colunas: descrição à esquerda, campos à direita. */
export function FormSection({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-x-10 gap-y-4 border-b border-line py-7 first:pt-0 last:border-b-0 md:grid-cols-[240px_minmax(0,1fr)]">
      <div>
        <h2 className="text-sm font-medium text-fg">{title}</h2>
        {description && <p className="mt-1 text-xs leading-relaxed text-fg-3">{description}</p>}
      </div>
      <div className="grid max-w-xl gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/** Estado de formulário com erros de campo vindos da API. */
export function useForm<T extends Record<string, string | number | boolean>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  return {
    values,
    errors,
    set<K extends keyof T>(key: K, value: T[K]) {
      setValues((v) => ({ ...v, [key]: value }));
      if (errors[key as string]) setErrors((e) => ({ ...e, [key]: '' }));
    },
    reset: (v: T) => setValues(v),
    handleError(err: unknown) {
      if (err instanceof ApiError) setErrors(err.fieldErrors());
    },
    clearErrors: () => setErrors({}),
  };
}

export function FormFooter({ children, error }: { children: ReactNode; error?: unknown }) {
  const message =
    error instanceof ApiError && Object.keys(error.fieldErrors()).length === 0
      ? error.message
      : error instanceof ApiError
        ? 'Revise os campos destacados.'
        : error
          ? 'Não foi possível salvar.'
          : null;
  return (
    <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-line bg-bg/95 px-4 py-3 backdrop-blur-sm lg:-mx-8 lg:px-8">
      {message && <p role="alert" className="mr-auto text-sm text-danger">{message}</p>}
      {children}
    </div>
  );
}
